import type { SiteContentEntity } from '@common/types/site-content.type';
import type { ShareBlocks } from '@common/types/site.type';
import type { UnmigratedBlock } from '@web/store/page-structure/page-structure/unmigrated-blocks';

import { createImmerReducer } from 'ngrx-immer/store';
import { on } from '@ngrx/store';

import { isObject } from '@common/helpers';

import PagesActions from '@web/store/website/pages/pages.actions';
import ContentActions from './content.actions';

export const WEBSITE_CONTENT_FEATURE_KEY = 'content';

export type ContentState = {
    site: SiteContentEntity;
    page: SiteContentEntity;
    error?: string;
    isDirty?: boolean;
    isLoading?: boolean;
    isReset?: boolean;
    dirtyPageId?: string | null;
    isAutoSavePaused?: boolean;
    /**
     * Per page, the version its content in this editor was read at — the save precondition. Kept here
     * rather than on the pages list, which a reload can refresh past the content it belongs to.
     */
    pageVersions: Record<string, string>;
    /** Pages whose save was refused with 409, until the reload it started brings the new version. */
    staleVersionPageIds: string[];
    /** Pages found deleted on the server while open here: nothing is saved to them any more. */
    deletedPageIds: string[];
    /** The open page's blocks that are kept off the canvas and written back as they were read. */
    unmigratedBlocks: UnmigratedBlock[];
};

export const initialContentState: ContentState = {
    site: null,
    page: null,
    isDirty: false,
    isLoading: false,
    isReset: false,
    dirtyPageId: null,
    isAutoSavePaused: false,
    pageVersions: {},
    staleVersionPageIds: [],
    deletedPageIds: [],
    unmigratedBlocks: [],
};

export const contentReducer = createImmerReducer<ContentState>(
    initialContentState,
    on(ContentActions.loadContent, ContentActions.loadPageContentRequest, (state: ContentState) => ({
        ...state,
        isLoading: true,
    })),
    on(ContentActions.loadSiteContentSuccess, (state: ContentState, { content }) => ({
        ...state,
        site: content,
        isLoading: false,
    })),
    on(ContentActions.loadSiteContentFailure, (state: ContentState, { error }) => ({ ...state, error })),
    on(ContentActions.loadPagesContentSuccess, (state: ContentState, { content }) => ({
        ...state,
        page: content,
        isLoading: false,
    })),
    on(ContentActions.loadPagesContentFailure, (state: ContentState, { error }) => ({ ...state, error })),
    on(ContentActions.setSharedBlocks, (state: ContentState, { blocks }) => {
        state.site.draft_shared_blocks = blocks;
        return state;
    }),
    on(ContentActions.setSharedBlock, (state: ContentState, { blockId, blockType }) => {
        state.site.draft_shared_blocks = sharedBlocksMap(state.site.draft_shared_blocks);
        state.site.draft_shared_blocks[blockType] = blockId;
        return state;
    }),
    on(ContentActions.removeSharedBlock, (state: ContentState, { blockType }) => {
        state.site.draft_shared_blocks = sharedBlocksMap(state.site.draft_shared_blocks);
        state.site.draft_shared_blocks[blockType] = null;
        return state;
    }),
    on(ContentActions.setDirtyPageId, (state: ContentState, { pageId }) => ({
        ...state,
        dirtyPageId: pageId?.toString(),
    })),
    on(ContentActions.setIsDirty, (state: ContentState, { isDirty }) => ({
        ...state,
        isDirty,
        dirtyPageId: isDirty ? state.dirtyPageId : null,
    })),
    on(ContentActions.setAutoSavePaused, (state: ContentState, { isPaused }) => ({
        ...state,
        isAutoSavePaused: isPaused,
    })),
    on(ContentActions.setPageVersion, (state: ContentState, { pageId, version }) => {
        state.pageVersions[String(pageId)] = version;
        state.staleVersionPageIds = state.staleVersionPageIds.filter((id) => id !== String(pageId));
        return state;
    }),
    on(ContentActions.markPageVersionStale, (state: ContentState, { pageId }) => {
        state.staleVersionPageIds = [...new Set([...state.staleVersionPageIds, String(pageId)])];
        return state;
    }),
    on(ContentActions.markPageDeleted, (state: ContentState, { pageId }) => {
        state.deletedPageIds = [...new Set([...state.deletedPageIds, String(pageId)])];
        return state;
    }),
    on(ContentActions.setUnmigratedBlocks, (state: ContentState, { blocks }) => ({
        ...state,
        unmigratedBlocks: blocks,
    })),
    on(ContentActions.setPageVersion, (state: ContentState, { pageId, version }) => {
        state.pageVersions[String(pageId)] = version;
        return state;
    }),
    on(ContentActions.updatePageStructureContent, (state: ContentState, { structure }) => {
        state.page.draft_structure = structure;
        return state;
    }),
    on(ContentActions.updateSiteThemeSettingsContent, (state: ContentState, { themeSettings }) => {
        state.site.draft_theme_settings = themeSettings;
        return state;
    }),
    on(ContentActions.updateSiteSharedBlocksContent, (state: ContentState, { sharedBlocks }) => {
        state.site.draft_shared_blocks = sharedBlocks;
        return state;
    }),
    on(ContentActions.resetContentToPublished, (state: ContentState) => {
        state.page.structure = state.page.draft_structure;
        state.site.theme_settings = state.site.draft_theme_settings;
        state.site.shared_blocks = state.site.draft_shared_blocks;
        return state;
    }),
    on(PagesActions.activatePageSuccess, (state: ContentState) => {
        state.page.structure_updated_at = new Date().toISOString();
        return state;
    }),
    on(ContentActions.setIsReset, (state: ContentState, { isReset }) => ({ ...state, isReset })),
    on(ContentActions.setIsLoading, (state: ContentState, { isLoading }) => ({ ...state, isLoading }))
);

// A site with no shared blocks can come back as `[]` — an array Immer refuses a `header` key on (#75695 bug 5).
function sharedBlocksMap(blocks: ShareBlocks): ShareBlocks {
    return isObject(blocks) ? blocks : { header: null, footer: null };
}
